/**
 * Single post "Table of contents" — matches nav items to H2 headings by text,
 * highlights the current section on scroll, and scrolls to a section on click.
 *
 * @return {void}
 */
export const initContentScrollNav = () => {

	const content = document.querySelector( '.js-content' );
	const nav = document.querySelectorAll( '.js-content-nav .c-toc__item' );

	if ( ! content || nav.length === 0 ) {

		return;

	}

	nav.forEach( ( navItem ) => {

		const id = navItem.dataset.id;
		const navText = navItem.textContent.trim();
		const heading = Array.from( content.querySelectorAll( 'h2' ) )
			.find( ( candidate ) => candidate.textContent.trim() === navText );

		if ( heading ) {

			heading.id = id;

		}

	} );

	const sections = Array.from( nav )
		.map( ( navItem ) => content.querySelector( `#${ navItem.dataset.id }` ) )
		.filter( Boolean );

	let lastScrollY = window.pageYOffset;
	let lastActiveIndex = -1;
	let isProgrammaticScroll = false;

	/**
	 * Mark a single nav item as active, clearing any previous active state.
	 *
	 * @param {number} index Index of the nav item to activate.
	 * @return {void}
	 */
	const setActive = ( index ) => {

		nav.forEach( ( navItem ) => navItem.classList.remove( 'is-active' ) );

		if ( nav[ index ] ) {

			nav[ index ].classList.add( 'is-active' );
			lastActiveIndex = index;

		}

	};

	const observer = new IntersectionObserver( ( entries ) => {

		if ( isProgrammaticScroll ) {

			return;

		}

		const currentScrollY = window.pageYOffset;
		const scrollingDown = currentScrollY > lastScrollY;

		entries.forEach( ( entry ) => {

			const index = sections.indexOf( entry.target );

			if ( entry.isIntersecting && scrollingDown ) {

				setActive( index );

			} else if ( ! entry.isIntersecting && ! scrollingDown && lastActiveIndex > 0 ) {

				setActive( lastActiveIndex - 1 );

			}

		} );

		lastScrollY = currentScrollY;

	}, { rootMargin: '-50% 0px -50% 0px' } );

	sections.forEach( ( section ) => observer.observe( section ) );

	/**
	 * Smooth-scroll to a table of contents section.
	 *
	 * @param {HTMLElement} navItem Clicked/activated nav item.
	 * @return {void}
	 */
	const scrollToSection = ( navItem ) => {

		const section = content.querySelector( `#${ navItem.dataset.id }` );

		if ( ! section ) {

			return;

		}

		setActive( Array.from( nav ).indexOf( navItem ) );

		const admin = document.getElementById( 'wpadminbar' );
		const offset = admin ? admin.offsetHeight : 0;
		const targetPosition = section.getBoundingClientRect().top + window.pageYOffset - offset - 16;

		isProgrammaticScroll = true;

		/**
		 * Resume scroll-spy once the programmatic scroll has settled.
		 *
		 * @return {void}
		 */
		const stopSuppressing = () => {

			isProgrammaticScroll = false;
			lastScrollY = window.pageYOffset;
			window.removeEventListener( 'scrollend', stopSuppressing );

		};

		window.addEventListener( 'scrollend', stopSuppressing );
		setTimeout( stopSuppressing, 1000 );

		window.scrollTo( {
			top: targetPosition,
			behavior: 'smooth',
		} );

	};

	nav.forEach( ( navItem ) => {

		navItem.addEventListener( 'click', () => scrollToSection( navItem ) );

		navItem.addEventListener( 'keydown', ( event ) => {

			if ( event.key === 'Enter' || event.key === ' ' ) {

				event.preventDefault();
				scrollToSection( navItem );

			}

		} );

	} );

};
